import PropTypes from 'prop-types';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import { Link, useHistory, useParams } from 'react-router-dom';
import { useTheme } from 'styled-components';
import {
  ConfirmModal,
  Icon,
  inkA,
  PageShell,
  SaveBar,
  SkeletonBar,
  tokens,
} from 'components/v3';
import { useAdminAccountVenues } from 'hooks/useAccountVenues';
import useAdminRouteContext from 'hooks/useAdminRouteContext';
import { fieldErrorsOf } from 'shared/apolloFieldErrors';
import { w9DashboardPath } from '../../W9/shared/routes';
import {
  useCreateSavedFormField,
  useDeleteSavedFormField,
  useGetFormAdmin,
  useGetSavedFormFields,
} from '../gql';
import { FormsHelp } from '../help';
import PreviewModal from '../Preview';
import { formsListPath } from '../shared/routes';
import { builderTourNavigate } from '../shared/tourNavigation';
import useFormsToaster from '../shared/useFormsToaster';
import ArchivedBanner from './ArchivedBanner';
import BuilderContext from './BuilderContext';
import DesktopBuilder from './desktop/DesktopBuilder';
import useAssetUpload from './hooks/useAssetUpload';
import useBuilderCommands, { scrollToField } from './hooks/useBuilderCommands';
import useBuilderLayout from './hooks/useBuilderLayout';
import useUnsavedGuard from './hooks/useUnsavedGuard';
import AddFieldSheet from './mobile/AddFieldSheet';
import LeaveSheet from './mobile/LeaveSheet';
import MobileBuilder from './mobile/MobileBuilder';
import builderReducer, {
  addFieldAction,
  addSavedFieldAction,
  isLockedId,
} from './state/builderReducer';
import { changePills, computeChanges, isDirty } from './state/diff';
import { savedFieldInput } from './state/savedFields';
import { definitionOf } from './state/snapshot';

// Settings → Forms → a form → Build (build plan §5.5–§5.9). Loads the form
// once, keeps an editable working copy in a reducer, and renders the
// desktop (palette | canvas | properties) or phone (single pane) builder.
// Nothing is written until Save (or the Live switch).
//
// The W-9 Settings page (w9-plan §6.5) mounts the same `FormBuilder` with
// `lockedFieldIds` (the IRS fields, read-only and immovable; new fields
// land in the payee slot), `renderHeader` (its own shell header in place
// of FormShellHeader) and `w9Redirect={false}`. The defaults leave the
// Forms builder exactly as it was.

const Centered = ({ children }) => {
  const theme = useTheme();
  const t = tokens(theme);
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 12,
        maxWidth: 520,
        margin: '48px auto',
        padding: '36px 24px',
        textAlign: 'center',
        borderRadius: 16,
        background: t.card,
        border: '1px solid ' + t.border,
        color: t.sub,
      }}
    >
      {children}
    </div>
  );
};

Centered.propTypes = { children: PropTypes.node.isRequired };

// 404 / 403 from getFormAdmin: the server's message and a way back. No
// builder chrome.
export const NotFound = ({ message }) => {
  const theme = useTheme();
  const t = tokens(theme);
  const { routePrefix } = useAdminRouteContext();
  return (
    <Centered>
      <Icon name="file-text" size={30} color={inkA(theme, 0.3)} />
      <h1 style={{ margin: 0, fontSize: 18, color: t.text }}>{message}</h1>
      <Link
        to={formsListPath(routePrefix)}
        style={{ color: t.primary, fontWeight: 700, textDecoration: 'none' }}
      >
        Back to Forms
      </Link>
    </Centered>
  );
};

NotFound.propTypes = { message: PropTypes.string.isRequired };

const Loading = () => (
  <div
    role="status"
    aria-busy="true"
    aria-label="Loading form"
    style={{ display: 'grid', gap: 16 }}
  >
    <SkeletonBar $w="100%" $h="64px" $r="16px" />
    <SkeletonBar $w="100%" $h="420px" $r="16px" />
  </div>
);

const venueName = (venues, id) => {
  const hit = venues.find((v) => v.id === id);
  return hit ? hit.name : null;
};

const useAccountName = (state, form, venues) =>
  venueName(venues, state.working.account_id) ||
  (form && form.account && form.account.id === state.working.account_id
    ? form.account.name
    : '');

// Uploaded images: the server's signed URLs plus this session's local
// previews (useAssetUpload owns those and revokes them when the builder
// closes).
const useAssetUrls = (form, localAssetUrls) =>
  useMemo(() => {
    const map = {};
    ((form && form.assets) || []).forEach((asset) => {
      if (asset.url) map[asset.id] = asset.url;
    });
    return { ...map, ...localAssetUrls };
  }, [form, localAssetUrls]);

const useSavedLibrary = (showToast) => {
  const { savedFields, loading } = useGetSavedFormFields();
  const createSaved = useCreateSavedFormField();
  const deleteSaved = useDeleteSavedFormField();
  const saveFieldForReuse = useCallback(
    async (field) => {
      const { data, error } = await createSaved(savedFieldInput(field));
      showToast(
        error
          ? { type: 'error', title: error.global }
          : `Saved “${data.name}” for reuse`
      );
    },
    [createSaved, showToast]
  );
  const removeSavedField = useCallback(
    async (saved) => {
      const { error } = await deleteSaved(saved.id);
      showToast(
        error
          ? { type: 'error', title: error.global }
          : 'Removed from Saved Fields'
      );
    },
    [deleteSaved, showToast]
  );
  return {
    savedFields,
    savedFieldsLoading: !!loading,
    saveFieldForReuse,
    removeSavedField,
  };
};

const toBarPills = (pills) =>
  pills.map((p) => ({
    key: p.key,
    label: p.label,
    count: p.count,
    static: p.static,
    fieldId: p.fieldId,
  }));

export const BuilderView = ({
  form,
  state,
  dispatch,
  refetch,
  renderHeader,
}) => {
  const history = useHistory();
  const { routePrefix } = useAdminRouteContext();
  const layout = useBuilderLayout();
  const { showToast, toasterNode } = useFormsToaster();
  const { venues } = useAdminAccountVenues();
  const library = useSavedLibrary(showToast);
  const [drawer, setDrawer] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const changes = useMemo(
    () => computeChanges(state.saved, state.working),
    [state.saved, state.working]
  );
  const dirty = isDirty(changes);
  const readOnly = state.archived;
  const accountName = useAccountName(state, form, venues);
  const assetUrls = useAssetUrls(form, state.localAssetUrls);
  const { upload, busy: uploading } = useAssetUpload(state.formId, dispatch);

  const guardRef = useRef(null);
  const navigate = useCallback(
    (path, routerState) => {
      if (guardRef.current) guardRef.current.release();
      history.push(path, routerState);
    },
    [history]
  );
  const closeOverlays = useCallback(() => setDrawer(null), []);
  const commands = useBuilderCommands({
    state,
    dispatch,
    refetch,
    showToast,
    closeOverlays,
    navigate,
    routePrefix,
  });
  const guard = useUnsavedGuard({
    dirty,
    onSave: commands.save,
    onDiscard: () => commands.discard({ quiet: true }),
  });
  guardRef.current = guard;

  const uploadAsset = useCallback(
    async (file) => {
      const result = await upload(file);
      if (result.error) showToast({ type: 'error', title: result.error });
      return result.assetId || null;
    },
    [upload, showToast]
  );

  const context = {
    state,
    dispatch,
    readOnly,
    kind: state.kind,
    lockedFieldIds: state.lockedFieldIds,
    isLocked: (id) => isLockedId(state, id),
    layout,
    assetUrls,
    uploadAsset,
    uploading,
    showToast,
    venues,
    accountName,
    ...library,
    onDuplicateForm: () => guard.guard(commands.duplicate),
    onArchiveForm: () => guard.guard(commands.requestArchive),
  };

  const header = {
    form: {
      id: state.formId,
      name: state.working.name || 'Untitled Form',
      status: state.status,
      archived_at: readOnly ? 'archived' : null,
      submission_count: (form && form.submission_count) || 0,
      account: {
        id: state.working.account_id || 0,
        name: accountName || 'Account',
      },
    },
    activeTab: 'build',
    dirty,
    // Saving holds the Live switch too: a toggle mid-save would race it.
    statusBusy: commands.statusBusy || commands.saving,
    onSetStatus: commands.setStatus,
    onPreview: () => setPreviewOpen(true),
  };

  const banner = readOnly ? (
    <ArchivedBanner onRestore={commands.restore} busy={commands.restoring} />
  ) : null;

  // The product tour's screen requests (help/FormsHelp). It never leaves
  // unsaved edits: with changes pending, list/submissions stay put.
  const firstField = state.working.fields[0];
  const tourNavigate = builderTourNavigate({
    formId: state.formId,
    fieldId: state.selectedId || (firstField ? firstField.id : null),
    dirty,
    layout: layout.mode,
    routePrefix,
    leaveTo: navigate,
    dispatch,
    setDrawer,
    closeOverlays: () => {
      setDrawer(null);
      setPreviewOpen(false);
      setAddOpen(false);
      if (layout.isMobile) dispatch({ type: 'SET_PANE', pane: 'canvas' });
    },
  });

  const onPill = (pill) => {
    dispatch({ type: 'SELECT', id: pill.fieldId || null });
    if (pill.fieldId) scrollToField(pill.fieldId);
    else if (typeof window.scrollTo === 'function') window.scrollTo(0, 0);
  };

  return (
    <BuilderContext.Provider value={context}>
      {layout.isMobile ? (
        <MobileBuilder
          header={header}
          banner={banner}
          dirty={dirty}
          renderHeader={renderHeader}
        />
      ) : (
        <DesktopBuilder
          header={header}
          banner={banner}
          renderHeader={renderHeader}
          drawer={drawer}
          onDrawer={setDrawer}
          onAddType={(fieldType) => dispatch(addFieldAction(fieldType))}
          onAddSaved={(saved) => dispatch(addSavedFieldAction(saved))}
          onOpenAddSheet={() => {
            dispatch({ type: 'SET_INSERT_TARGET', target: { mode: 'end' } });
            setAddOpen(true);
          }}
        />
      )}
      {dirty && !readOnly ? (
        <SaveBar
          count={changes.total}
          pills={toBarPills(changePills(state.saved, state.working, changes))}
          onPill={onPill}
          onDiscard={() => commands.discard()}
          onSave={() => commands.save()}
          saving={commands.saving}
          hint={state.status === 'live' ? 'Saving updates the live form' : null}
        />
      ) : null}
      {layout.isMobile ? null : (
        <AddFieldSheet
          open={addOpen}
          onClose={() => {
            setAddOpen(false);
            dispatch({ type: 'SET_INSERT_TARGET', target: null });
          }}
        />
      )}
      <LeaveSheet
        open={!!guard.pending}
        count={changes.total}
        onSave={guard.saveAndLeave}
        onDiscard={guard.discardAndLeave}
        onKeep={guard.keepEditing}
      />
      <ConfirmModal
        open={commands.confirm === 'archive'}
        icon="archive"
        confirmIcon="archive"
        confirmLabel="Archive"
        title={`Archive “${state.saved.name}”?`}
        message="It's hidden in the app and on the web, and moves to Archived."
        onConfirm={commands.archive}
        onCancel={commands.cancelConfirm}
      />
      <ConfirmModal
        open={commands.confirm === 'reload'}
        icon="history"
        confirmIcon="history"
        confirmLabel="Discard and reload"
        title="Reload and lose your changes?"
        message="Someone else saved this form. Reloading shows their version and drops your unsaved edits."
        onConfirm={commands.reload}
        onCancel={commands.cancelConfirm}
      />
      <PreviewModal
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        form={{
          name: state.working.name,
          description: state.working.description,
          account: { name: accountName },
          show_progress: state.working.show_progress,
        }}
        definition={definitionOf(state.working)}
        assetUrls={assetUrls}
      />
      {toasterNode}
      <FormsHelp page="builder" navigate={tourNavigate} />
    </BuilderContext.Provider>
  );
};

BuilderView.propTypes = {
  form: PropTypes.object,
  state: PropTypes.object.isRequired,
  dispatch: PropTypes.func.isRequired,
  refetch: PropTypes.func.isRequired,
  // (headerProps) → node, in place of FormShellHeader (w9-plan §6.5).
  renderHeader: PropTypes.func,
};

BuilderView.defaultProps = { form: null, renderHeader: undefined };

// A W-9 form never opens in the regular builder (w9-plan §6.1): its
// Settings page does the editing. The id's account picks the section.
const W9Redirect = ({ form }) => {
  const history = useHistory();
  const { routePrefix } = useAdminRouteContext();
  useEffect(() => {
    history.replace(
      w9DashboardPath(routePrefix, {
        account: form.account ? form.account.id : null,
      })
    );
  }, [history, routePrefix, form]);
  return <Loading />;
};

W9Redirect.propTypes = { form: PropTypes.object.isRequired };

export const FormBuilder = ({
  formId,
  lockedFieldIds,
  renderHeader,
  w9Redirect,
}) => {
  const { form, error, refetch } = useGetFormAdmin(formId);
  const [state, dispatch] = useReducer(builderReducer, null);
  const redirect = w9Redirect && !!form && form.kind === 'w9';
  // Initialise once per form. A background refetch never replaces the
  // working copy; only SAVED or an explicit Reload does.
  const initedFor = useRef(null);
  useEffect(() => {
    if (
      !redirect &&
      form &&
      form.id === formId &&
      initedFor.current !== form.id
    ) {
      initedFor.current = form.id;
      dispatch({ type: 'INIT', form, lockedFieldIds });
    }
  }, [form, formId, lockedFieldIds, redirect]);

  if (redirect) return <W9Redirect form={form} />;
  if (state && state.formId === formId) {
    return (
      <BuilderView
        form={form}
        state={state}
        dispatch={dispatch}
        refetch={refetch}
        renderHeader={renderHeader}
      />
    );
  }
  if (error && !form) return <NotFound message={fieldErrorsOf(error).global} />;
  return <Loading />;
};

FormBuilder.propTypes = {
  formId: PropTypes.number.isRequired,
  // Fields the builder shows locked (w9-plan §6.5). Default: none.
  lockedFieldIds: PropTypes.arrayOf(PropTypes.string),
  renderHeader: PropTypes.func,
  // Send a `w9` form to the W-9 section (the regular builder page does).
  w9Redirect: PropTypes.bool,
};

FormBuilder.defaultProps = {
  lockedFieldIds: undefined,
  renderHeader: undefined,
  w9Redirect: true,
};

// Keyed by id so opening another form (e.g. after Duplicate) starts fresh.
const FormBuilderPage = () => {
  const { id } = useParams();
  const formId = Number(id);
  return (
    <PageShell>
      <FormBuilder key={formId} formId={formId} />
    </PageShell>
  );
};

export default FormBuilderPage;
